import React from 'react';
import { Lock } from 'lucide-react';

/**
 * "You may not see this" is not the same fact as "there is nothing here", and
 * rendering the second when the first is true is how a permission change gets
 * reported as data loss.
 *
 * API PR #358 put `requireRole('admin','office_staff')` on every `/pay-profiles`
 * route. The three screens that read it had no error branch at all, so a 403
 * collapsed into the empty state — and each of those empty states then offered a
 * button to CREATE the record that already exists and the caller simply could not
 * see. A dispatcher would have read "No worker pay profiles yet" as a fact about
 * the company.
 *
 * Three states, never two: forbidden, failed, and genuinely empty. `FieldPayScreen`
 * already draws this distinction inline (`is403` → Lock + message → generic error →
 * empty); this is the same shape as a component so the office screens cannot each
 * invent their own. That screen is NOT converted to use this — it is field-styled
 * against `fieldiq-*` tokens and lives in a different route tree, and converting it
 * would be drive-by work on a file this change has no reason to touch.
 */
export function PermissionNotice({
  title = "You don't have permission to view pay profiles",
  detail = 'Pay rates are visible to admins and office staff. Ask an admin if you need access.',
  className = '',
}: {
  title?: string;
  detail?: string;
  className?: string;
}) {
  return (
    <div className={`text-center py-16 px-4 ${className}`}>
      <Lock size={40} className="mx-auto mb-3 text-gray-500" />
      <p className="text-foreground font-semibold">{title}</p>
      <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">{detail}</p>
    </div>
  );
}

/** True when an axios rejection is specifically a permissions refusal. */
export function isForbidden(error: unknown): boolean {
  return (error as any)?.response?.status === 403;
}

export default PermissionNotice;
